/**
 * useMessages — server-driven alerts/banners/badges fetcher.
 *
 * Spec: arkheus/docs/specs/cuidari/t7-messaging-badges-banners.md §5.
 *
 * Returns the visible messages for the given placement slot plus a dismiss()
 * action. Implements stale-while-revalidate (60s) via TanStack Query and
 * pauses background revalidation when the tab is hidden.
 *
 * The visible-list optimism lives in the render surfaces (they hide
 * the card in local state on dismiss), so the hook just persists the
 * dismissal to the backend and invalidates the query on success — the next
 * refetch reflects the canonical state.
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { AuthContext } from '@/lib/cerberus/AuthContext'
import { useTrack } from '@/lib/analytics'
import { dismissMessage, listMessages } from './api'
import { sanitizeMessages, type DroppedMessage } from './sanitize'
import type { MessagesResponse, PlacementSlot, ServerMessage } from './types'

const STALE_MS = 60_000
const DEV_CONSOLE_AUTH = import.meta.env.DEV && import.meta.env.VITE_CONSOLE_DEV_AUTH === '1'
const REAL_ADMIN_PREVIEW = import.meta.env.VITE_PHEME_REAL_ADMIN === '1'
const LOCAL_PHEME_FALLBACK = DEV_CONSOLE_AUTH && !REAL_ADMIN_PREVIEW

/**
 * Reads tenant + user from AuthContext without throwing when the
 * provider is missing. Some surfaces (e.g. the onboarding placeholder
 * during the B.3→B.4 transition, or unit tests) can render
 * before/without `<AuthProvider>` (pre-auth flows); in that case we
 * degrade to "no tenant → no fetch → empty messages" instead of
 * crashing the component tree.
 *
 * Tenant resolution mirrors `finance/hooks::useActiveTenantId` — B2B
 * picks `activeOrgId`; B2C falls back to `user.id` when the user has
 * at least one personal grant (no org_id, user_id matches self). Without
 * this fallback, every Pheme placement on B2C accounts skipped the fetch
 * (because `activeOrgId` is null) and no banners rendered.
 */
function useActiveAuth(): { tenantId: string | null; userId: string | null } {
  const ctx = useContext(AuthContext)
  if (!ctx || ctx.status !== 'authed' || !ctx.user) {
    return { tenantId: null, userId: null }
  }
  if (ctx.activeOrgId) {
    return { tenantId: ctx.activeOrgId, userId: ctx.user.id }
  }
  const hasPersonalGrant = ctx.grants.some(
    (g) => !g.org_id && g.user_id === ctx.user!.id,
  )
  return {
    tenantId: hasPersonalGrant ? ctx.user.id : null,
    userId: ctx.user.id,
  }
}

function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState<boolean>(
    typeof document === 'undefined' ? true : document.visibilityState !== 'hidden',
  )
  useEffect(() => {
    if (typeof document === 'undefined') return
    const onChange = () => setVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])
  return visible
}

const messagingKey = (placement: PlacementSlot, contextKey: string, userId: string | null) =>
  ['messaging', placement, userId ?? 'anon', contextKey] as const

/**
 * Per-render dedupe set for RUM `pheme.message_dropped` events. The
 * sanitizer can fire on every refetch (60s SWR + visibility-change
 * refresh), so a noisy debug placement would otherwise emit one envelope
 * per minute per open tab. The set lives in a ref so the dedupe survives
 * re-renders without inflating across mount/unmount cycles.
 */
function useDropReporter() {
  const track = useTrack()
  const seen = useRef<Set<string>>(new Set())
  return useCallback(
    (placement: PlacementSlot, dropped: DroppedMessage) => {
      const key = `${dropped.message_id}::${dropped.reason}`
      if (seen.current.has(key)) return
      seen.current.add(key)
      track({
        component_id: `messaging.${placement}`,
        component_type: 'banner',
        action: 'pheme.message_dropped',
        metadata: {
          message_id: dropped.message_id,
          placement_slot: dropped.placement_slot,
          expected_placement: dropped.expected_placement,
          reason: dropped.reason,
        },
      })
    },
    [track],
  )
}

interface UseMessagesResult {
  messages: ServerMessage[]
  dismiss: (messageId: string) => Promise<void>
  isLoading: boolean
  error: Error | null
  fetchedAt: string | null
}

interface UseMessagesOptions {
  enabled?: boolean
}

export function useMessages(
  placement: PlacementSlot,
  context?: Record<string, unknown>,
  options: UseMessagesOptions = {},
): UseMessagesResult {
  const { tenantId, userId } = useActiveAuth()
  const qc = useQueryClient()
  const visible = useDocumentVisible()
  const enabled = options.enabled ?? true
  const reportDrop = useDropReporter()

  const contextKey = useMemo(() => {
    if (!context) return ''
    try { return JSON.stringify(context) } catch { return '' }
  }, [context])

  const queryKey: QueryKey = messagingKey(placement, contextKey, userId)

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }): Promise<MessagesResponse> => {
      if (LOCAL_PHEME_FALLBACK) {
        return { placement, messages: [], fetched_at: new Date().toISOString() }
      }
      if (!tenantId) return { placement, messages: [], fetched_at: new Date().toISOString() }
      return listMessages(tenantId, placement, context, signal)
    },
    enabled: LOCAL_PHEME_FALLBACK || (!!tenantId && visible && enabled),
    staleTime: STALE_MS,
    refetchInterval: visible && enabled ? STALE_MS : false,
    refetchOnWindowFocus: true,
  })

  const dismissMutation = useMutation({
    mutationFn: async (messageId: string) => {
      if (!tenantId) throw new Error('No active tenant — cannot dismiss')
      return dismissMessage(tenantId, messageId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messaging'] })
    },
  })

  const rawMessages = query.data?.messages
  const messages = useMemo(
    () =>
      sanitizeMessages(rawMessages ?? [], placement, {
        onDrop: (dropped) => reportDrop(placement, dropped),
      }),
    [rawMessages, placement, reportDrop],
  )

  return {
    messages,
    dismiss: (messageId: string) => dismissMutation.mutateAsync(messageId).then(() => {}),
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
    fetchedAt: query.data?.fetched_at ?? null,
  }
}
